import { Component, EnvironmentInjector, HostBinding, inject, input, OnDestroy, OnInit, runInInjectionContext, signal } from '@angular/core';
import { Subscription, filter, switchMap, take } from 'rxjs';

import { CmsService } from '@spartacus/core';

import { ImageDsService, VideoDsService } from '@seb/web-design-system/core';

import { BaseStoreService, LazyLoadingService, UrlMappingService } from '../../services';

@Component({
  template: '',
  standalone: true
})
export class AbstractPageComponent implements OnDestroy, OnInit {
  class = input('');

  @HostBinding('class')
  get hostClasses(): string {
    return [this.class(), 'wc-template'].filter(Boolean).join(' ');
  }

  private readonly environmentInjector = inject(EnvironmentInjector);

  protected readonly lazyLoadingService = inject(LazyLoadingService);
  protected readonly urlMappingService = inject(UrlMappingService);
  protected readonly cmsService = inject(CmsService);

  protected readonly subscriptions = new Subscription();

  protected readonly snippetImages = signal<any[]>([]);
  protected readonly snippetVideos = signal<any[]>([]);

  protected snippetsOptions?: { images?: boolean; videos?: boolean };
  protected eagerLoadingSlot = '';
  protected eagerLoadingLimit = 2;

  ngOnInit(): void {
    this.handleUrlTranslation();

    if (this.eagerLoadingSlot) {
      this.handleEagerLoadingUids();
    } else {
      this.lazyLoadingService.setFirstUids([]);
    }

    if (this.snippetsOptions?.images) {
      this.loadImageSnippets();
    }

    if (this.snippetsOptions?.videos) {
      this.loadVideoSnippets();
    }
  }

  private handleUrlTranslation(): void {
    this.subscriptions.add(
      this.cmsService.getCurrentPage().pipe(
        filter((p) => !!p),
        switchMap(({ label }) => this.urlMappingService.getUrlMappingByCode(label)),
        filter((u) => !!u)
      ).subscribe((urlMapping) => {
        this.urlMappingService.translateUrl(urlMapping);
      })
    );
  }

  private handleEagerLoadingUids() {
    this.subscriptions.add(
      this.cmsService.getContentSlot(this.eagerLoadingSlot).pipe(
        take(1)
      ).subscribe((slotData) => {
        this.lazyLoadingService.setFirstUids(
          slotData?.components
            ?.filter(({ typeCode }) => typeCode !== 'AnchorBlocComponent')
            .slice(0, this.eagerLoadingLimit)
            .map(({ uid }) => uid) || []
        );
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadImageSnippets(): void {
    runInInjectionContext(this.environmentInjector, () => {
      const imageDsService = inject(ImageDsService);

      this.subscriptions.add(
        imageDsService.loadImageSnippets().subscribe((images) => {
          this.snippetImages.set(images);
        })
      );
    });
  }

  private loadVideoSnippets(): void {
    runInInjectionContext(this.environmentInjector, () => {
      const baseStoreService = inject(BaseStoreService);
      const videoDsService = inject(VideoDsService);

      this.subscriptions.add(
        videoDsService.loadVideoSnippets(baseStoreService.baseStore.brandSite).subscribe((videos) => {
          this.snippetVideos.set(videos);
        })
      );
    });
  }
}
